import React, { useEffect } from "react";
import { Button, Space, Typography } from "antd";
import { DownloadOutlined, CloseOutlined } from "@ant-design/icons";
import { usePWAInstall } from "../../../hooks/usePWAInstall";
import styles from "./styles.module.scss";

const { Text } = Typography;

interface PWAInstallBannerProps {
  className?: string;
  position?: "top" | "bottom";
}

export const PWAInstallBanner: React.FC<PWAInstallBannerProps> = ({
  className = "",
  position = "top",
}) => {
  const { showBanner, install, dismissBanner, isInstalled, forcePWADetection } =
    usePWAInstall();

  // Trigger PWA detection on component mount (after route changes)
  useEffect(() => {
    // Small delay to ensure page is fully loaded
    const timer = setTimeout(() => {
      forcePWADetection();
    }, 1000);

    return () => clearTimeout(timer);
  }, []); // Run once on mount

  // Don't show banner if app is already installed
  if (isInstalled || !showBanner) {
    return null;
  }

  const handleInstall = async () => {
    try {
      await install();
    } catch (error) {
      console.error("Install failed:", error);
    }
  };

  const handleDismiss = () => {
    dismissBanner();
  };

  return (
    <div
      className={`${styles.pwaInstallBanner} ${
        styles[`pwaInstallBanner--${position}`]
      } ${className}`}
      role="banner"
      aria-label="Install app banner"
    >
      <div className={styles.pwaInstallBannerContent}>
        <div className={styles.pwaInstallBannerIcon}>
          <img
            src="/favicon_preview_192.png"
            alt="Circadia App"
            className={styles.pwaInstallBannerAppIcon}
          />
        </div>

        <div className={styles.pwaInstallBannerText}>
          <Text strong className={styles.pwaInstallBannerTitle}>
            Install Circadia Intelligence Platform App
          </Text>
        </div>

        <Space className={styles.pwaInstallBannerActions}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleInstall}
            className={styles.pwaInstallBannerInstallBtn}
            size="small"
          >
            Install
          </Button>

          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={handleDismiss}
            className={styles.pwaInstallBannerDismissBtn}
            size="small"
            aria-label="Dismiss install banner"
          />
        </Space>
      </div>
    </div>
  );
};
